import { useEffect } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import useSiteSettings from '@/hooks/useSiteSettings';

const FONT_HEADING = "'Cormorant Garamond', Cambria, Georgia, serif";
const FONT_BODY = "'Jost', sans-serif";

/**
 * Single reusable legal-page renderer. Both /terms and /privacy use this.
 * `slug` selects which set of settings keys to read (terms_* or privacy_*).
 */
export default function LegalPage({ slug }) {
  const s = useSiteSettings() || {};
  const prefix = slug === 'privacy' ? 'privacy' : 'terms';
  const fallbackHeadline = slug === 'privacy' ? 'Privacy & Cookie Policy' : 'Terms & Conditions';

  const eyebrow = s[`${prefix}_page_eyebrow`] || 'Legal';
  const headline = s[`${prefix}_page_headline`] || fallbackHeadline;
  const subtitle = s[`${prefix}_page_subtitle`] || '';
  const lastUpdated = s[`${prefix}_last_updated`] || '';
  const content = s[`${prefix}_content`] || '';

  useEffect(() => {
    const brand = s.brand_name || 'Sàwai Capital';
    document.title = `${headline} — ${brand}`;
  }, [s.brand_name, headline]);

  // Render the content as paragraphs. We use plain newlines: each block separated by
  // a blank line is a paragraph; if the first line of a block is short and ALL-CAPS or
  // starts with a number "1. ", treat it as a heading.
  const renderContent = (text) => {
    if (!text) return null;
    const blocks = text.split(/\n\s*\n/);
    return blocks.map((block, i) => {
      const trimmed = block.trim();
      if (!trimmed) return null;
      // Detect heading: first line looks like "1. TITLE" or all caps
      const firstNewline = trimmed.indexOf('\n');
      const firstLine = firstNewline === -1 ? trimmed : trimmed.slice(0, firstNewline);
      const rest = firstNewline === -1 ? '' : trimmed.slice(firstNewline + 1).trim();
      const isHeading = /^\d+\.\s+[A-Z]/.test(firstLine) || (firstLine.length < 80 && firstLine === firstLine.toUpperCase() && firstLine.length > 3);

      if (isHeading && rest) {
        return (
          <div key={i} className="mb-8" data-testid={`legal-section-${i}`}>
            <h2 className="text-xl md:text-2xl text-[#A0743A] mb-3 tracking-tight" style={{ fontFamily: FONT_HEADING }}>{firstLine}</h2>
            <div className="text-[#999] text-base leading-relaxed whitespace-pre-line" style={{ fontFamily: FONT_BODY }}>{rest}</div>
          </div>
        );
      }
      return (
        <p key={i} className="text-[#999] text-base leading-relaxed mb-6 whitespace-pre-line" style={{ fontFamily: FONT_BODY }}>{trimmed}</p>
      );
    });
  };

  return (
    <div className="bg-[#1A1A1A] min-h-screen" data-testid={`legal-page-${prefix}`}>
      <Navbar />

      {/* Hero */}
      <section className="pt-36 pb-12 px-6 md:px-12 border-b border-[#A0743A]/15">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>{eyebrow}</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-[#FAF8F5] mb-6 tracking-tight leading-[1.1]" style={{ fontFamily: FONT_HEADING }}>{headline}</h1>
          {subtitle && (
            <>
              <div className="w-16 h-[1px] bg-[#C9A96E] mx-auto mb-6" />
              <p className="text-[#999] text-base md:text-lg max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: FONT_BODY }}>{subtitle}</p>
            </>
          )}
          {lastUpdated && (
            <p className="mt-6 text-[#666] text-xs uppercase tracking-[0.2em]" style={{ fontFamily: FONT_BODY }}>{lastUpdated}</p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          {content ? renderContent(content) : (
            <p className="text-[#666] text-center text-sm italic" style={{ fontFamily: FONT_BODY }}>Content coming soon.</p>
          )}
        </div>
      </section>

      <Footer s={s} />
    </div>
  );
}
